import React from 'react';
import type { ColumnsType } from 'antd/es/table';
import { LoadingOutlined } from '@ant-design/icons';
import { PERMISSIONS } from 'constants/permissions';
import { AddItemModal } from 'pages/admin/items/addItem';
import { HasAccess } from 'components/common/accessControl';
import styles from 'pages/admin/clients/styles.module.scss';
import { SupplierOfferStatusBadge } from 'components/common/StatusBadge';
import { FilterIcon, PlusIcon, VerifiedIcon, ViewIcon } from 'components/common/icons';
import { Button, Col, Form, FormInstance, Input, PageTitle, Row, Table, Typography } from 'components';
import { useSupplierOffers } from './useSupplierOffers';
import { Filters } from './filters';
import { Link } from 'react-router-dom';
import { ADMIN_ROUTES } from 'constants/routes';
import { BrandsDrawer } from 'pages/admin/purchaseOrders/brandsDrawer';
import { SupplierOfferType } from 'models/SupplierOffer';

const { Search } = Input;
const { Text } = Typography;

type EntityTableType = {
  name: string;
  id: string;
};

export interface SupplierOfferDataType {
  id: string;
  uuid: string | number;
  itemSKU: string;
  validUntil: string;
  brand: EntityTableType;
  supplier: EntityTableType;
  averagePrice: number;
  createDate: string;
  lastModifiedDate: string;
  leadTimeMin: string;
  leadTimeMax: string;
  leadTime: string;
  relatedEntityId: string;
  relatedEntityUuid: string;
  type: SupplierOfferType;
}

export type LinkedSupplierOfferType = Exclude<SupplierOfferType, 'NONE'>;

const LINKED_ENTITY_MAPPING: Record<LinkedSupplierOfferType, string> = {
  PURCHASE_REQUEST: 'PR',
  PURCHASE_ORDER: 'PO',
};

export const SupplierOffers: React.FC = () => {
  const [filtersForm] = Form.useForm<FormInstance>();
  const {
    addModalState,
    onAddModalStateChange,
    onCloseFilters,
    onOpenFilters,
    openFilters,
    handleTableChange,
    routeChange,
    isLoading,
    pagination,
    onSearch,
    data,
    total,
    applyFilters,
    resetFilters,
    onBrandDrawerExpand,
    brandDrawerState,
    brandsData,
  } = useSupplierOffers();
  const columns: ColumnsType<SupplierOfferDataType> = [
    {
      title: 'Offer Number',
      dataIndex: 'id',
      key: 'id',
      render: (_, { uuid }) => {
        return uuid;
      },
    },
    {
      title: 'Supplier',
      dataIndex: 'supplier',
      key: 'supplier',
      width: 200,
      render: (supplier) => {
        return (
          <Row align={'middle'} wrap={false} className={'gap-12'}>
            {supplier.verified && <VerifiedIcon color={'#05796B'} size={16} />}
            <Link to={`${ADMIN_ROUTES.suppliers.view}/${supplier.id}/general`} target={'_blank'}>
              <Text underline type={'secondary'} ellipsis={{ tooltip: true }}>
                {supplier.name}
              </Text>
            </Link>
          </Row>
        );
      },
    },
    {
      title: 'Brand',
      dataIndex: 'brands',
      key: 'brands',
      width: 200,
      render: (brands) => {
        return (
          <Row align={'middle'} wrap={false} justify={'space-between'}>
            {!!brands?.length && (
              <Link to={`${ADMIN_ROUTES.brand.view}/${brands[0].id}/management`} target={'_blank'}>
                <Text underline type={'secondary'} ellipsis={{ tooltip: true }}>
                  {brands[0].name}
                </Text>
              </Link>
            )}
            {brands?.length > 1 && (
              <Button
                size={'small'}
                type={'text'}
                variant={'text'}
                className={'underline-button'}
                onClick={() => onBrandDrawerExpand(brands)}
              >
                <Text underline type={'secondary'}>
                  +{brands?.length - 1}
                </Text>
              </Button>
            )}
          </Row>
        );
      },
    },
    {
      title: 'Valid Until',
      dataIndex: 'formattedValidityDate',
      key: 'validity',
      render: (value) => value?.day,
      minWidth: 130,
    },
    {
      title: 'Linked Entity',
      dataIndex: 'type',
      key: 'type',
      render: (type: SupplierOfferType, record) => {
        const { relatedEntityId, relatedEntityUuid } = record;
        if (type === 'NONE') {
          return '';
        }
        return (
          <Link
            to={`${ADMIN_ROUTES[type === 'PURCHASE_REQUEST' ? 'purchaseRequest' : 'purchaseOrder'].view}/${relatedEntityId}/general`}
            target={'_blank'}
          >
            <Text underline type={'secondary'} ellipsis={{ tooltip: true }}>
              {LINKED_ENTITY_MAPPING[type]}: {relatedEntityUuid}
            </Text>
          </Link>
        );
      },
      minWidth: 130,
    },
    {
      title: 'Status',
      dataIndex: 'published',
      key: 'published',
      minWidth: 130,
      render: (published) => <SupplierOfferStatusBadge type={published ? 'PUBLISH' : 'DRAFT'} />,
    },
    {
      title: 'Created By',
      dataIndex: 'createdByUser',
      key: 'createdByUser',
      width: 150,
      render: (value) => {
        if (value) {
          return (
            <Text style={{ maxWidth: '150px' }} ellipsis={{ tooltip: true }}>
              {`${value.firstName || ''} ${value.lastName || ''}`.trim()}
            </Text>
          );
        }
        return '';
      },
    },
    {
      title: 'Created Date',
      dataIndex: 'formattedCreatedDate',
      key: 'createDate',
      render: (value) => value?.day,
      minWidth: 160,
      sorter: true,
    },
    {
      title: 'Total Amount',
      dataIndex: 'totalAmount',
      key: 'totalAmount',
      minWidth: 100,
      sorter: true,
    },
    {
      title: 'Lead time',
      dataIndex: 'leadTime',
      key: 'leadTime',
      minWidth: 110,
      render: (value, record) => {
        const { leadTimeMax, leadTimeMin } = record;
        return leadTimeMin && leadTimeMax
          ? `${leadTimeMin}-${leadTimeMax} ${value === 'DAY' ? 'days' : 'weeks'}`
          : leadTimeMin
            ? `${leadTimeMin} Min`
            : leadTimeMax
              ? `${leadTimeMax} Max`
              : '';
      },
    },
    {
      fixed: 'right',
      width: '36px',
      key: 'actions',
      render: (value, record) => {
        const { type } = record;
        return <Button onClick={() => routeChange(value.id, type)} type={'text'} size={'small'} icon={<ViewIcon />} />;
      },
    },
  ];

  return (
    <Col span={24} className={styles.main}>
      <Row gutter={[0, 24]}>
        <Col span={24}>
          <Row align={'middle'} justify={'space-between'}>
            <PageTitle title={'Supplier Offer'} />
            <HasAccess role={PERMISSIONS.supplierOffers.supplierOffer.write}>
              <Button
                type={'primary'}
                disabled={true}
                onClick={() => onAddModalStateChange(true)}
                icon={<PlusIcon color={'#FFFFFF'} />}
              >
                Add New
              </Button>
            </HasAccess>
          </Row>
        </Col>
        <Col span={24} className={styles.main_container}>
          <Row>
            <Col span={24}>
              <Row justify={'end'} className={styles.actions_container}>
                <Col style={{ width: '300px' }}>
                  <Search enterButton={false} placeholder={'Search'} onChange={(e) => onSearch(e.target.value)} />
                </Col>
                <Button color="default" variant="filled" onClick={onOpenFilters} icon={<FilterIcon />} />
              </Row>
            </Col>
            <Col span={24} style={{ paddingTop: '20px' }}>
              <HasAccess role={PERMISSIONS.supplierOffers.supplierOffer.read}>
                <Table
                  tableLayout={'auto'}
                  bordered
                  scroll={{
                    x: true,
                  }}
                  rowKey="id"
                  size={'small'}
                  dataSource={data}
                  columns={columns}
                  loading={{
                    indicator: <LoadingOutlined style={{ fontSize: 48 }} spin />,
                    spinning: isLoading,
                  }}
                  locale={
                    isLoading
                      ? { emptyText: null } // Hide "No Data" text while loading
                      : {} // Use default AntD "No Data" icon when empty
                  }
                  onChange={handleTableChange}
                  pagination={{
                    position: ['bottomCenter'],
                    showSizeChanger: true,
                    showTotal: (total) => `Total ${total} items`,
                    size: 'default',
                    current: pagination.page,
                    pageSize: pagination.size,
                    total: total,
                  }}
                />
              </HasAccess>
            </Col>
          </Row>
        </Col>
      </Row>
      <Filters
        form={filtersForm}
        drawerOpen={openFilters}
        closeDrawer={onCloseFilters}
        applyFilters={applyFilters}
        resetFilters={resetFilters}
      />
      <AddItemModal modalState={addModalState} onModalStateChange={onAddModalStateChange} />
      <BrandsDrawer onExpand={onBrandDrawerExpand} drawerState={brandDrawerState} data={brandsData} />
    </Col>
  );
};
